import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

// Module-level cache so every instance shares one fetch of the ~2,300 US universities.
let CACHE = null;

export function UniversityAutocomplete({
  value,
  onChange,
  onPick,
  onSubmit,
  placeholder = "Type your university…",
  testid = "university-input",
  excludeNames,
  inputClassName = "w-full rounded-md border border-border bg-background pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-brand outline-none",
}) {
  const [list, setList] = useState(CACHE);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const mouseMoved = useRef(false); // only let hover set highlight after a real cursor move

  const load = () => {
    if (CACHE) { setList(CACHE); return; }
    fetch("/us_universities.json")
      .then((r) => r.json())
      .then((d) => { CACHE = d; setList(d); })
      .catch(() => { CACHE = []; setList([]); });
  };

  const q = (value || "").trim().toLowerCase();
  const excl = excludeNames || new Set();
  const matches = (q.length >= 2 && list && list.length)
    ? list
        .filter((u) => !excl.has(u.n.toLowerCase()) && u.n.toLowerCase().includes(q))
        .sort((a, b) => {
          const ai = a.n.toLowerCase().startsWith(q) ? 0 : 1;
          const bi = b.n.toLowerCase().startsWith(q) ? 0 : 1;
          return ai - bi || a.n.length - b.n.length;
        })
        .slice(0, 8)
    : [];

  useEffect(() => { setHi(-1); }, [value]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const choose = (u) => {
    onChange(u.n);
    if (onPick) onPick(u);
    setOpen(false);
    setHi(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      mouseMoved.current = false;
      if (!open) { setOpen(true); return; }
      if (matches.length) setHi((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      mouseMoved.current = false;
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && hi >= 0 && matches[hi]) { e.preventDefault(); choose(matches[hi]); }
      else if (onSubmit) { setOpen(false); setHi(-1); onSubmit(); }
    } else if (e.key === "Escape") {
      setOpen(false); setHi(-1);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <Search size={14} strokeWidth={1.8} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
      <input
        data-testid={testid}
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-controls={`${testid}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={hi >= 0 ? `${testid}-opt-${hi}` : undefined}
        value={value}
        onFocus={() => { load(); setOpen(true); }}
        onChange={(e) => { load(); mouseMoved.current = false; setHi(-1); onChange(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={inputClassName}
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          id={`${testid}-listbox`}
          data-testid={`${testid}-listbox`}
          onMouseMove={() => (mouseMoved.current = true)}
          className="absolute left-0 right-0 top-full mt-1 z-[1500] max-h-64 overflow-auto rounded-md border border-border bg-white shadow-lg py-1"
        >
          {matches.map((u, i) => (
            <li
              key={u.n + i}
              id={`${testid}-opt-${i}`}
              role="option"
              aria-selected={hi === i}
              data-testid={`${testid}-opt-${i}`}
              onMouseEnter={() => { if (mouseMoved.current) setHi(i); }}
              onMouseDown={(e) => { e.preventDefault(); choose(u); }}
              className={`px-3 py-2 text-sm cursor-pointer truncate ${hi === i ? "bg-brand/10 text-brand" : "hover:bg-secondary"}`}
            >
              {u.n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
